import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiGrid, FiUpload, FiDownload, FiRotateCcw, FiImage } from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';

const SIZE = 12;

const Page = styled.div`
  padding: 28px 24px;
  max-width: 760px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
`;

const UploadZone = styled.label`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 22px;
  border: 1.5px dashed rgba(147,51,234,0.35);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-align: center;
  &:hover { border-color: rgba(147,51,234,0.6); background: rgba(147,51,234,0.05); }
  svg { font-size: 24px; color: #a78bfa; }
  .label { font-size: 13.5px; font-weight: 600; color: #e2e8f0; }
  .hint { font-size: 11.5px; color: #6b7280; }
  input { display: none; }
`;

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  .preview-title { font-size: 12px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.04em; }
`;

const PixelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${SIZE}, 1fr);
  width: min(336px, 80vw);
  aspect-ratio: 1 / 1;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const PixelCell = styled.div`
  aspect-ratio: 1 / 1;
  background-color: ${p => p.color};
`;

const NoImage = styled.div`
  width: min(336px, 80vw);
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  border: 1px dashed rgba(255,255,255,0.08);
  border-radius: 4px;
  color: #374151;
  svg { font-size: 34px; }
  span { font-size: 12.5px; color: #4b5563; }
`;

const BtnRow = styled.div`
  display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.45 : 1};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const PixelBuilder = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const [pixels, setPixels] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);

      const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
      const cells = [];
      for (let i = 0; i < data.length; i += 4) {
        const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3] / 255];
        cells.push(`rgba(${r}, ${g}, ${b}, ${a})`);
      }
      setPixels(cells);
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas || !pixels) return;

    // JPEG has no alpha channel — paint a white background first so
    // transparent pixels don't turn black in the exported file.
    const jpgCanvas = document.createElement('canvas');
    jpgCanvas.width = SIZE;
    jpgCanvas.height = SIZE;
    const ctx = jpgCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.drawImage(canvas, 0, 0);

    const link = document.createElement('a');
    link.download = '12x12.jpg';
    link.href = jpgCanvas.toDataURL('image/jpeg', 0.92);
    link.click();
  };

  const handleReset = () => {
    setPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon"><FiGrid /></div>
          <div>
            <div className="title">{t('website.pixel_builder.title')}</div>
            <div className="sub">{t('website.pixel_builder.sub')}</div>
          </div>
        </PageHeader>

        <Card>
          <UploadZone>
            <FiUpload />
            <div className="label">{t('website.pixel_builder.upload_label')}</div>
            <div className="hint">{t('website.pixel_builder.upload_hint')}</div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} />
          </UploadZone>

          <PreviewWrap>
            <div className="preview-title">{t('website.pixel_builder.preview_title')}</div>
            {pixels ? (
              <PixelGrid>
                {pixels.map((color, idx) => <PixelCell key={idx} color={color} />)}
              </PixelGrid>
            ) : (
              <NoImage>
                <FiImage />
                <span>{t('website.pixel_builder.no_image')}</span>
              </NoImage>
            )}
          </PreviewWrap>

          <BtnRow>
            <Btn type="button" onClick={handleDownload} disabled={!pixels}>
              <FiDownload /> {t('website.pixel_builder.download_btn')}
            </Btn>
            <Btn type="button" variant="reset" onClick={handleReset} disabled={!pixels}>
              <FiRotateCcw /> {t('website.pixel_builder.reset_btn')}
            </Btn>
          </BtnRow>
        </Card>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Page>
    </WebsiteLayout>
  );
};

export default PixelBuilder;
